"use client";

import { useCategoryNavigationPrefetch } from "@faire/retailer-visitor-shared/lib/useCategoryNavigationPrefetch";
import { assignSettingOnEvent } from "@faire/retailer-visitor-shared/settings/assignSettingOnEvent";
import { Column } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { cn } from "@faire/web/tools/cn";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { Link } from "@faire/web/ui/LinkAnchor";
import { trackCategoriesSideNavigationClick } from "@faire/web-api--source/events/categories/click/sideNavigation";
import { INavigationTree } from "@faire/web-api--source/indigofair/data/INavigationTree";
import React from "react";

import INavigationTreeLink = INavigationTree.INavigationTreeLink;

const SDUIFilterablePaginatedSectionNavigationLinkBase: React.FC<{
  navigationTreeLink: INavigationTreeLink;
}> = ({ navigationTreeLink }) => {
  const CATEGORY_PREFETCH_SURFACE_TYPE = "sideNav";
  const viewRef = useDoActionWhenVisible(() => {
    navigationTreeLink.identifiers_for_assignment.forEach((setting) =>
      assignSettingOnEvent(setting)
    );
  });

  const {
    prefetchOnMouseEnter,
    prefetchOnTouchStart,
    clearPrefetchTimeoutOnBlur,
  } = useCategoryNavigationPrefetch(CATEGORY_PREFETCH_SURFACE_TYPE);

  return (
    <Column align="flex-start" justify="flex-start">
      <Link
        to={navigationTreeLink.url ?? ""}
        className="flex h-fit min-h-8 w-full flex-row items-center justify-start select-none"
        aria-current={navigationTreeLink.selected ? "page" : undefined}
        onClick={() =>
          trackCategoriesSideNavigationClick(
            navigationTreeLink.identifier ?? ""
          )
        }
        ref={viewRef}
        onMouseEnter={() => prefetchOnMouseEnter(navigationTreeLink.url)}
        onTouchStart={() => prefetchOnTouchStart(navigationTreeLink.url)}
        onMouseLeave={clearPrefetchTimeoutOnBlur}
        style={{ outline: "revert" }}
      >
        <Typography
          className={cn(
            "text-core-text-default border-b border-solid pb-[2px]",
            navigationTreeLink.selected
              ? "border-core-border-default"
              : "border-transparent",
            "hover:border-core-border-hover hover:text-core-text-enabled hover:border-b hover:border-solid hover:font-medium",
            "focus:border-core-border-hover focus:text-core-text-enabled focus:border-b focus:border-solid focus:font-medium"
          )}
          variant={
            navigationTreeLink.selected
              ? "paragraphSansMedium"
              : "paragraphSansRegular"
          }
        >
          {navigationTreeLink.name ?? ""}
        </Typography>
      </Link>
      {navigationTreeLink.children.length ? (
        <ul className="flex w-full flex-col items-start pl-4">
          {navigationTreeLink.children.map((child) => (
            <li style={{ width: "100%" }} key={child.identifier}>
              <SDUIFilterablePaginatedSectionNavigationLink
                navigationTreeLink={child}
              />
            </li>

            // TODO(Bella): add handling to new tag
          ))}
        </ul>
      ) : null}
    </Column>
  );
};

export const SDUIFilterablePaginatedSectionNavigationLink = React.memo(
  SDUIFilterablePaginatedSectionNavigationLinkBase
);
